import { useEffect, useRef } from 'react'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { prefersReducedMotion } from './animation-config'

gsap.registerPlugin(ScrollTrigger)

/**
 * Color waypoints for each act section
 * HSL values for smooth interpolation
 */
const ATMOSPHERE_COLORS = [
  { h: 222, s: 47, l: 8 }, // Act 1: Software (slate-950)
  { h: 33, s: 80, l: 10 }, // Act 2: Real Estate (amber-950)
  { h: 270, s: 60, l: 10 }, // Act 3: Music (violet-950)
  { h: 152, s: 60, l: 10 }, // Act 4: Personal (emerald-950)
  { h: 215, s: 40, l: 12 }, // Epilogue (slate-900)
]

/**
 * Scroll-driven atmosphere color transitions
 * Interpolates CSS custom properties across acts for smooth color morphing
 */
export function useAtmosphere(mainRef: React.RefObject<HTMLElement | null>) {
  const overlayRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const main = mainRef.current
    const overlay = overlayRef.current
    if (!main || !overlay || prefersReducedMotion()) return

    // Set initial CSS custom properties
    overlay.style.setProperty('--atm-h', String(ATMOSPHERE_COLORS[0].h))
    overlay.style.setProperty('--atm-s', String(ATMOSPHERE_COLORS[0].s))
    overlay.style.setProperty('--atm-l', String(ATMOSPHERE_COLORS[0].l))

    const ctx = gsap.context(() => {
      // Get all act sections
      const sections = main.querySelectorAll('section')
      if (sections.length < 2) return

      // Create transitions between consecutive sections
      sections.forEach((section, i) => {
        if (i >= ATMOSPHERE_COLORS.length - 1) return
        const next = ATMOSPHERE_COLORS[i + 1]
        if (!next) return

        gsap.to(overlay, {
          '--atm-h': next.h,
          '--atm-s': next.s,
          '--atm-l': next.l,
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            start: 'bottom 60%',
            end: 'bottom 20%',
            scrub: 1,
          },
        })
      })
    }, main)

    return () => ctx.revert()
  }, [mainRef])

  return overlayRef
}
